import {
	createJSONRPCErrorResponse,
	createJSONRPCRequest,
	createJSONRPCSuccessResponse,
	isJSONRPCRequest,
	isJSONRPCResponse,
	JSONRPCErrorCode,
	type JSONRPCRequest,
	type JSONRPCResponse,
} from 'json-rpc-2.0';

import { type ClientId } from '../client/bridge-api-client-types';
import {
	type CommandConstructor,
	type CommandProperties,
	type CommandResult,
} from '../command/bridge-api-command-types';

/**
 * Strongly typed RPC request, sent over JavaScript contexts.
 *
 * @description Represents a delegated method call using the command pattern.
 * @see {@link https://gameprogrammingpatterns.com/command.html}
 *
 * @description Data is serialised over-the-wire in the JSON RPC 2.0 protocol: {@link https://www.jsonrpc.org/specification}.
 *
 * @example
 * ```json
 *	{
 *  	"jsonrpc": "2.0",
 *  	"id": "rpc:app-content:app-chat:FindCommand:",
 *  	"method": "FindCommand",
 *  	"params": {
 *			"sourceId": "app-content",
 *			"targetId": "app-chat"
 *		}
 *	}
 * ```
 */
export type RPCRequest = Omit<JSONRPCRequest, 'params'> & {
	id: RPCIdentifier;
	params: CommandProperties;
};

/**
 * Strongly-typed RPC response, sent over JavaScript contexts.
 *
 * @description Represents the result of a delegated method call.
 * @description Data is serialised over-the-wire in the JSON RPC 2.0 protocol: {@link https://www.jsonrpc.org/specification}.
 *
 * @example
 * ```json
 *	{
 *		"jsonrpc": "2.0",
 *		"id": "rpc:app-content:app-chat:PingPongCommand:pKI_V2Wn9-wb3kQehoDyB",
 *		"result": "pong",
 *		"params": {
 *			"sourceId": "app-chat",
 *			"targetId": "app-content"
 *		}
 *	}
 * ```
 */
export type RPCResponse = Omit<JSONRPCResponse, 'params'> & {
	id: RPCIdentifier;
	params: CommandProperties;
};

/**
 * Strongly-typed RPC message which can be sent over-the-wire between JavaScript contexts.
 */
export type RPCMessage = RPCRequest | RPCResponse;

/**
 * Unique identifier for an RPC message.
 */
export type RPCIdentifier = string & { __rpcId: symbol };

/**
 * A unique ID for this specific command invocation.
 * @param commandClass
 * @param commandProperties
 * @returns unique ID string with parts separated by `:`
 */
export function createInvocationId(
	commandClass: CommandConstructor,
	commandProperties: CommandProperties,
) {
	const parts = [
		'rovo-bridge-api',
		commandProperties.senderId,
		commandProperties.receiverId,
		commandClass.identifier,
		commandProperties._invocationId ?? 'unspecified-id',
	];
	return parts.join(':') as RPCIdentifier;
}

// ----------------------------------------------------
// #region  Raw data -> RPC shape (on sender's side)
// ----------------------------------------------------

/**
 * Converts a command invocation to an {@link RPCRequest}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#request_object}.
 *
 * @param id unique ID for this command invocation
 * @param commandClass class of the command to be invoked
 * @param commandProperties parameters of the command
 * @returns {RPCRequest}
 */
export function rpcCommand<C extends CommandProperties, R extends CommandResult>(
	id: RPCIdentifier,
	commandClass: CommandConstructor<C, R>,
	commandProperties: C,
): RPCRequest {
	return createJSONRPCRequest(id, commandClass.identifier, commandProperties) as RPCRequest;
}

/**
 * Converts a successful command invocation result to an {@link RPCResponse}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#response_object}
 *
 * @param id unique ID for this command invocation
 * @param senderId client which handled the request, and provided the response
 * @param receiverId client which sent the request, and is receiving the response
 * @param result result of the invocation, as handled by the sender
 * @returns {RPCResponse}
 */
export function rpcSuccess<R extends CommandResult>(
	id: string,
	senderId: ClientId,
	receiverId: ClientId,
	result: R,
): RPCResponse {
	const response = createJSONRPCSuccessResponse(id, result);
	return { ...response, params: { senderId, receiverId } } as RPCResponse;
}

/**
 * Converts an unsuccessful command invocation result to an {@link RPCResponse}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#error_object}
 *
 * @param id unique ID for this command invocation
 * @param senderId client which handled the request, and provided the response
 * @param receiverId client which sent the request, and is receiving the response
 * @param result result of the invocation, as handled by the sender
 * @returns {RPCResponse}
 */
export function rpcError(
	id: string,
	senderId: ClientId,
	receiverId: ClientId,
	code: JSONRPCErrorCode | undefined = JSONRPCErrorCode.InternalError,
	error: Error,
): RPCResponse {
	const response = createJSONRPCErrorResponse(id, code, error.message, error.stack);
	return { ...response, params: { senderId, receiverId } } as RPCResponse;
}

// ----------------------------------------------------
// #region  Raw data -> RPC shape (on receiver's side)
// ----------------------------------------------------

/**
 * Type guard which checks if a given shape is an {@link RPCRequest}.
 *
 * @description MUST provide an ID for resiliency and observability purposes.
 *
 * @param request a shape which is possibly an RPC request
 * @returns whether {@link request} is an {@link RPCRequest} or not
 */
export function isRPCRequest(request: unknown): request is RPCRequest {
	return Boolean(isJSONRPCRequest(request) && request.id && typeof request.id === 'string');
}

/**
 * Type guard which checks if a given shape is an {@link RPCResponse}.
 *
 * @description MUST provide an ID for deferred awaiting on the result of an invocation.
 *
 * @param request a shape which is possibly an RPC request
 * @returns whether {@link request} is an {@link RPCRequest} or not
 */
export function isRPCResponse(response: unknown): response is RPCResponse {
	return Boolean(isJSONRPCResponse(response) && response.id && typeof response.id === 'string');
}
